/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	OnInit,
	OnDestroy,
	TemplateRef,
	ViewChild,
	inject,
} from '@angular/core';
import {
	Observable,
	Subject,
	combineLatest,
	map,
	merge,
	of,
	startWith,
	take,
	takeUntil,
	withLatestFrom,
} from 'rxjs';
import { ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/common/material/material-module';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import {
	ChecklistDataMap,
	IChecklistItemDataWithId,
} from 'src/app/shared/ui/material/checkbox-list/checkbox-list-model';
import { CheckboxListModelService } from 'src/app/shared/ui/material/checkbox-list/checkbox-list-model.service';

import * as appStrings from '@touchplan/strings';
import { IImportVariances, ITicketOverrides, Ticket, TicketOverride } from '@touchplan/common';
import { LearnMoreDialogService } from '../../services/learn-more-dialog.service';
import { ImportTicketsCommonService } from '../../services/import-tickets-common.service';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { getSwimlaneBelonging } from 'src/app/utils/ticket-utils';
import { DialogReusableModule } from 'src/app/common/dialog-reusable/dialog-reusable.module';

enum ModeSelectValue {
	Assign = 'assign',
	Exclude = 'exclude',
}

@Component({
	selector: 'import-tickets-variances',
	standalone: true,
	templateUrl: './import-tickets-variances.component.html',
	styleUrls: ['./import-tickets-variances.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LearnMoreDialogService],
	imports: [
		CommonModule,
		MaterialModule,
		PresentationModule,
		ReactiveFormsModule,
		SharedUIMaterialModule,
		MatDialogModule,
		DialogReusableModule,
	],
})
export class ImportTicketsVariancesComponent implements OnInit, OnDestroy {
	private importTicketsCommonService = inject(ImportTicketsCommonService);
	private bannerMessageHandler = inject(BannerMessageHandler);

	public appStrings = appStrings;

	private destroyed$: Subject<void> = new Subject();

	protected modeSelectOptions$ = of(
		new Map([
			[ModeSelectValue.Assign, appStrings.IMPORT_TICKET_MAP_VARIANCE_REASONS_STEP_MODE_SELECT_ASSIGN],
			[ModeSelectValue.Exclude, appStrings.IMPORT_TICKET_MAP_VARIANCE_REASONS_STEP_MODE_SELECT_EXCLUDE],
		])
	);

	protected formGroup: FormGroup<{
		modeSelect: FormControl<ModeSelectValue>;
		varianceReason: FormControl<string>;
		varianceComment: FormControl<string>;
		needVarianceCheckList: FormControl<ChecklistDataMap>;
	}> = this.fb.group({
		modeSelect: [ModeSelectValue.Assign],
		varianceReason: [''],
		varianceComment: [''],
		needVarianceCheckList: [],
	});

	protected varianceList$ = this.importTicketsStateService.varianceList;

	private ticketsCheckListDataMap$: Observable<ChecklistDataMap> = combineLatest([
		this.importTicketsStateService.needsVariance$,
		this.importTicketsStateService.ticketList,
		this.importTicketsStateService.swimlanesList,
	]).pipe(
		withLatestFrom(this.needVarianceCheckList.valueChanges.pipe(startWith(new Map()))),
		map(([[ticketIds, ticketList, swimlaneList], ticketsCheckListDataMap]) => {
			ticketIds.forEach((ticketId) => {
				const ticket = ticketList.get(ticketId);
				if (ticket) {
					const swimlaneBelonging = getSwimlaneBelonging(ticket.view.top, swimlaneList);
					if (ticketsCheckListDataMap.has(ticketId)) {
						ticketsCheckListDataMap.get(ticketId).originalModel.ticket = ticket;
						ticketsCheckListDataMap.get(ticketId).originalModel.swimlaneBelonging = swimlaneBelonging;
					} else {
						ticketsCheckListDataMap.set(ticketId, {
							isChecked: false,
							originalModel: {
								ticket,
								swimlaneBelonging,
								varianceReason: '',
								varianceComment: '',
								icon: 'error-icon',
								iconClass: 'ticket-need-variance-icon',
								isExcluded: false,
							},
						});
					}
				}
			});

			return ticketsCheckListDataMap;
		})
	);

	private reOrderTicketsCheckList$: Subject<void> = new Subject<void>();

	protected ticketsCheckList$: Observable<IChecklistItemDataWithId[]> = merge(
		this.ticketsCheckListDataMap$,
		this.modeSelect.valueChanges,
		this.reOrderTicketsCheckList$
	).pipe(
		map(() => {
			return CheckboxListModelService.convertMapToArray(this.needVarianceCheckList.value).sort((a, b) => {
				// Empty varianceReason and isExcluded is false (Place at the top)
				if (a.originalModel.varianceReason === '' && a.originalModel.isExcluded === false) return -1;
				if (b.originalModel.varianceReason === '' && b.originalModel.isExcluded === false) return 1;

				// Non-empty varianceReason and isExcluded is false (Place at the bottom)
				if (a.originalModel.varianceReason !== '' && a.originalModel.isExcluded === false) return 1;
				if (b.originalModel.varianceReason !== '' && b.originalModel.isExcluded === false) return -1;

				// isExcluded is true (Place in the middle)
				if (a.originalModel.isExcluded === true && b.originalModel.isExcluded === false) return 1;
				if (a.originalModel.isExcluded === false && b.originalModel.isExcluded === true) return -1;

				// Default case: maintain the order
				return 0;
			});
		})
	);

	@ViewChild('importCannotBeProcessedTemplate') importCannotBeProcessedTemplate: TemplateRef<any>;

	constructor(
		private importTicketsStateService: ImportTicketsStateService,
		private fb: FormBuilder,
		private learnMoreDialogService: LearnMoreDialogService
	) {}

	get modeSelect(): FormControl<string> {
		return this.formGroup.controls['modeSelect'];
	}

	get needVarianceCheckList(): FormControl<ChecklistDataMap> {
		return this.formGroup.controls['needVarianceCheckList'];
	}

	get varianceReason(): FormControl<string> {
		return this.formGroup.controls['varianceReason'];
	}

	get varianceComment(): FormControl<string> {
		return this.formGroup.controls['varianceComment'];
	}

	ngOnInit(): void {
		this.ticketsCheckListDataMap$
			.pipe(take(1), takeUntil(this.destroyed$))
			.subscribe((ticketsCheckListDataMap) => {
				this.needVarianceCheckList.setValue(ticketsCheckListDataMap);
			});

		// This subcription "listen" for changes in the modeSelect dropdown to perform 2 things:
		// 1. "un-checks" all the checkboxes.
		// 2. Disables or enables the varianceReason and the varianceComment fields based on the value of the dropdown.
		this.modeSelect.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((modeSelectValue) => {
			const needVarianceCheckListValue = this.needVarianceCheckList.value;
			needVarianceCheckListValue.forEach((ticketCheck) => (ticketCheck.isChecked = false));
			this.needVarianceCheckList.setValue(needVarianceCheckListValue);

			if (modeSelectValue !== ModeSelectValue.Assign) {
				this.varianceReason.disable();
				this.varianceComment.disable();
			} else {
				this.varianceReason.enable();
				this.varianceComment.enable();
			}
		});
	}

	/* This method is getting called only upon the user's clicking of the "Apply" button within the template.
	 * This is important because the `needVarianceCheckList.setValue` should exclusively be associated with user interaction events;
	 * otherwise, there is a risk of additional emissions occurring, leading to undesirable behaviors.
	 */
	protected onApplyVarianceReason() {
		const modeSelectValue = this.modeSelect.value;
		const isThereAtLeastOneTicketSelected = [...this.needVarianceCheckList.value?.values()].some(
			(checkListItem) => checkListItem?.isChecked
		);
		if (
			isThereAtLeastOneTicketSelected &&
			((modeSelectValue === ModeSelectValue.Assign && !!this.varianceReason.value) ||
				modeSelectValue === ModeSelectValue.Exclude)
		) {
			const varianceCheckListMap = this.needVarianceCheckList.value;
			for (const [_, checkListItem] of varianceCheckListMap) {
				if (checkListItem.isChecked) {
					if (modeSelectValue === ModeSelectValue.Assign) {
						checkListItem.originalModel.isExcluded = false;
						checkListItem.originalModel.varianceReason = this.varianceReason.value;
						checkListItem.originalModel.varianceComment = this.varianceComment.value;
						checkListItem.originalModel.icon = 'check_circle';
						checkListItem.originalModel.iconClass = 'ticket-has-variance-icon';
					} else if (modeSelectValue === ModeSelectValue.Exclude) {
						checkListItem.originalModel.isExcluded = true;
						checkListItem.originalModel.varianceReason = '';
						checkListItem.originalModel.varianceComment = '';
						checkListItem.originalModel.icon = 'block';
						checkListItem.originalModel.iconClass = '';
					}
				}
				checkListItem.isChecked = false;
			}

			this.needVarianceCheckList.setValue(varianceCheckListMap);
			this.varianceReason.reset();
			this.varianceComment.setValue('');
			this.reOrderTicketsCheckList$.next();
		} else if (modeSelectValue === ModeSelectValue.Assign && !this.varianceReason.value) {
			this.bannerMessageHandler.displayWarningMessage('Please select a variance reason');
		} else if (!isThereAtLeastOneTicketSelected) {
			this.bannerMessageHandler.displayWarningMessage('Please select at least one ticket');
		}
	}

	protected onBack(): void {
		this.importTicketsStateService.previousStep();
	}

	protected onNext(): void {
		const needsVarianceCheckListDataMap: ChecklistDataMap = this.formGroup.value.needVarianceCheckList;
		const variances: IImportVariances = {};
		const ticketOverrides: ITicketOverrides = {};
		let showImportCannotBeProcessedDialog = false;

		for (const [key, checkListItem] of needsVarianceCheckListDataMap) {
			if (!checkListItem.originalModel?.isExcluded) {
				const id = checkListItem.originalModel?.varianceReason;
				const comment = checkListItem.originalModel?.varianceComment;
				if (id) {
					variances[key] = { id, comment };
				} else {
					showImportCannotBeProcessedDialog = true;
					break;
				}
			} else {
				ticketOverrides[key] = [TicketOverride.CUT_DATES];
			}
		}

		if (showImportCannotBeProcessedDialog) {
			this.onLearnMoreClick(this.importCannotBeProcessedTemplate);
			return;
		}
		this.importTicketsStateService.nextStep({ variances, ticketOverrides });
	}

	protected onLearnMoreClick(template: TemplateRef<any>): void {
		this.learnMoreDialogService.openDialog(template);
	}

	protected focusTicket(ticket: Ticket): void {
		if (!ticket) {
			return;
		}

		this.importTicketsCommonService.focusTicket$.next(ticket);
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
